import type { IndustrySeoContent } from "~/utils/seo-industry-content";
import { seoPageFontClass } from "~/utils/seo-game-content";
import { getBreadcrumbListJsonLd, getFaqPageJsonLd } from "~/utils/seo-meta";
import { seoRouteLabel } from "~/utils/seo-routes";
import leftQuoteIcon from "~/assets/icons/left-quote.svg";

const fontClass = seoPageFontClass;

interface IndustrySeoPageProps {
  content: IndustrySeoContent;
}

export function IndustrySeoPage({ content }: IndustrySeoPageProps) {
  const { slug, headline, subhead, longDescription, painPoints, howPrizoHelps, recommendedGames, faq, caseStudyQuote } = content;
  const pagePath = `/for/${slug}`;

  return (
    <div className="bg-[#ffffff] w-full">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: seoRouteLabel(pagePath) ?? headline, path: pagePath },
            ]),
          ),
        }}
      />
      {faq?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getFaqPageJsonLd(faq, pagePath)),
          }}
        />
      )}
      {/* Hero */}
      <div className="seo-section">
        <div className="seo-hero-head">
          <h1 className="seo-h1">
            {headline}
          </h1>
          <p className="seo-lead">
            {subhead}
          </p>
        </div>
        <div className="seo-cta-row">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-primary"
          >
            Try For Free
          </a>
          <a
            href="https://calendly.com/nadav-prizo/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-secondary"
          >
            Book a Demo
          </a>
        </div>
      </div>

      {/* Long description */}
      {longDescription && (
        <div className="seo-band-grey">
          <div className="max-w-[720px] mx-auto px-4 md:px-[120px]">
            <div className="seo-body leading-[1.75] space-y-4">
              {longDescription.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pain Points */}
      <div className="seo-band-violet">
        <div className="seo-wrap">
          <h2 className="seo-h2">
            Industry Challenges
          </h2>
          <ul className="seo-list">
            {painPoints.map((point, i) => (
              <li key={i} className="seo-li">
                <span className="text-[#696cff] font-bold">•</span>
                <span className="seo-body">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* How Prizo Helps */}
      <div className="seo-section">
        <h2 className="seo-h2">
          How Prizo Helps
        </h2>
        <ul className="seo-list">
          {howPrizoHelps.map((item, i) => (
            <li key={i} className="seo-li">
              <span className="text-[#696cff] mt-1">✓</span>
              <span className="seo-body">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommended Games */}
      <div className="bg-[rgba(105,108,255,0.06)] w-full py-12 md:py-16">
        <div className="seo-wrap">
          <h2 className="seo-h2">
            Recommended Games for Your Industry
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {recommendedGames.map((game, i) => (
              <span
                key={i}
                className={`${fontClass} px-5 py-2.5 bg-white rounded-2xl text-[#696cff] font-medium text-[14px] md:text-[16px] shadow-sm`}
              >
                {game}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Case study / social proof quote */}
      {caseStudyQuote && (
        <div className="seo-wrap py-12 md:py-16">
          <div className="max-w-[720px] mx-auto bg-[#F4F4FF] rounded-2xl p-6 md:p-8">
            <img src={leftQuoteIcon} alt="" className="w-10 h-10 mb-4" />
            <p className={`${fontClass} text-[#6A6A6A] text-[15px] md:text-[16px] leading-[1.6] italic`}>
              "{caseStudyQuote}"
            </p>
          </div>
        </div>
      )}

      {/* FAQ */}
      {faq?.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">
            Frequently Asked Questions
          </h2>
          <div className="seo-faq-list">
            {faq.map((item, i) => (
              <div key={i} className="seo-faq-item">
                <h3 className="seo-h3">
                  {item.question}
                </h3>
                <p className="seo-muted">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="seo-wrap py-12 md:py-16 text-center">
        <a
          href="https://apps.shopify.com/prizo"
          target="_blank"
          rel="noopener noreferrer"
          className="seo-btn-cta"
        >
          Install on Shopify — Free Trial
        </a>
      </div>
    </div>
  );
}
